import { useEffect, useId, useRef, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { useTranslation } from 'react-i18next'
import { cn } from '../../lib/cn'
import { useStatusBarDim } from '../../lib/theme'
import { IconClose } from '../icons'

/**
 * Popup. Desktop: centered dialog (max 520px, `wide` 720px). Phones: bottom sheet with a handle.
 * Escape and the backdrop close it; focus moves into the dialog and back to the opener.
 */
export function Modal({ open, title, onClose, children, footer, wide = false, description }: {
  open: boolean; title: ReactNode; onClose(): void; children: ReactNode; footer?: ReactNode; wide?: boolean; description?: ReactNode
}) {
  const { t } = useTranslation()
  const titleId = useId()
  useStatusBarDim(open)
  const descId = useId()
  const box = useRef<HTMLDivElement>(null)
  // Callers usually pass an inline `onClose`, a new function on every render. Reading it through a
  // ref keeps the effect below tied to `open` only: re-running it per keystroke would move focus
  // back to the first field and break IME composition (Vietnamese Telex/VNI input).
  const closeRef = useRef(onClose)
  closeRef.current = onClose
  useEffect(() => {
    if (!open) return
    const opener = document.activeElement as HTMLElement | null
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeRef.current()
      if (e.key === 'Tab' && box.current) {
        const f = [...box.current.querySelectorAll<HTMLElement>('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])')].filter(x => !x.hasAttribute('disabled'))
        if (!f.length) return
        if (e.shiftKey && document.activeElement === f[0]) { e.preventDefault(); f[f.length - 1].focus() }
        else if (!e.shiftKey && document.activeElement === f[f.length - 1]) { e.preventDefault(); f[0].focus() }
      }
    }
    window.addEventListener('keydown', onKey)
    requestAnimationFrame(() => (box.current?.querySelector<HTMLElement>('input, textarea, select') ?? box.current)?.focus())
    const prev = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => { window.removeEventListener('keydown', onKey); document.body.style.overflow = prev; opener?.focus?.() }
  }, [open])

  if (!open) return null

  return createPortal(
    <div className="fixed inset-x-0 top-0 z-50 flex h-[var(--app-h)] items-end justify-center sm:items-center sm:p-4">
      <button
        type="button"
        tabIndex={-1}
        aria-label={t('common.close')}
        className="absolute inset-0 h-full w-full bg-[rgb(20_20_18_/_0.4)] dark:bg-[rgb(0_0_0_/_0.55)] cursor-pointer border-0 p-0"
        onClick={(e) => {
          e.preventDefault()
          e.stopPropagation()
          onClose()
        }}
      />
      <div
        ref={box} role="dialog" aria-modal="true" aria-labelledby={titleId} aria-describedby={description ? descId : undefined} tabIndex={-1}
        className={cn('relative z-10 flex max-h-[88vh] w-full flex-col overflow-hidden bg-cream-50 shadow-pop border border-line outline-none pb-[env(safe-area-inset-bottom,0px)] sm:pb-0',
          'rounded-t-[22px] max-sm:border-b-0 sm:rounded-2xl', wide ? 'sm:max-w-[720px]' : 'sm:max-w-[520px]')}
      >
        <div className="mx-auto mt-2.5 h-[5px] w-10 shrink-0 rounded-full bg-cream-300 sm:hidden" aria-hidden="true" />
        {/* No rules around header and footer (docs/mockups/modal-compact.html, direction B): the box
            reads as one surface and the spacing does the separating. A scrolling body clips at its own
            edge, so the missing hairline never leaves text floating under the header. */}
        <header className="flex items-start gap-3 px-5 pt-4 pb-1">
          <div className="min-w-0 flex-1">
            <h2 id={titleId} className="text-[15px] font-semibold leading-snug text-ink-100">{title}</h2>
            {description && <p id={descId} className="mt-0.5 text-xs text-ink-400">{description}</p>}
          </div>
          <button
            type="button"
            aria-label={t('common.close')}
            onClick={onClose}
            className="-mr-1 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-ink-400 hover:bg-cream-200 hover:text-ink-100"
          >
            <IconClose className="h-4 w-4" />
          </button>
        </header>
        <div className="min-h-0 overflow-y-auto px-5 py-2">{children}</div>
        {footer && (
          // One row on every screen, right-aligned like the desktop dialog, and the phone buttons drop to
          // `sm` (30px): the stacked 44px pair cost ~128px of a bottom sheet for two short actions.
          // The box already carries the phone safe-area padding, so the footer only adds its 16px.
          <footer className="flex flex-row justify-end gap-2 px-5 pt-1 pb-4 [&>button]:max-sm:h-[30px] [&>button]:max-sm:px-2.5 [&>button]:max-sm:text-[12.5px]">
            {footer}
          </footer>
        )}
      </div>
    </div>,
    document.body
  )
}
